import { appWithTranslation } from 'next-i18next'
import { Lato } from 'next/font/google'
import { AuthContextProvider } from '@/contexts/useAuth'
import NextNProgress from 'nextjs-progressbar'
import type { AppProps } from 'next/app'

import { instance } from '@/lib/http/axios'
import analyticsService from '@/lib/services/analytics'

import '@/styles/globals.scss'
import { ReactElement, ReactNode, useEffect } from 'react'
import { NextPage } from 'next'
import Layout from '@/components/layout/Layout'
import { Toaster } from '@/components/ui/toaster'
import Head from 'next/head'
import { useRouter } from 'next/router'

const font = Lato({
  weight: ['100', '300', '400', '900', '700'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
})

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

function App({ Component, pageProps: { ...pageProps } }: AppPropsWithLayout) {
  const router = useRouter()

  // Set initial Accept-Language header
  instance.defaults.headers.common['Accept-Language'] =
    pageProps._nextI18Next?.initialLocale ?? 'en'

  // Update Accept-Language header when locale changes
  useEffect(() => {
    if (router.locale) {
      instance.defaults.headers.common['Accept-Language'] = router.locale
    }
  }, [router.locale])

  // Initialize Mixpanel once on app mount
  useEffect(() => {
    analyticsService.initialize()
  }, [])

  // Track page views from Next's router events. We do this instead of Mixpanel's
  // built-in `track_pageview`, which monkey-patches history.pushState/replaceState;
  // on Chrome-for-iOS that wrapper collides with the browser's own history wrapper
  // (and Clarity/Sentry) into a recursion cycle that overflows the stack
  // (Sentry ALOESUITE-PROD-BR). routeChangeComplete does not fire for the initial
  // load, so we also track once on mount.
  useEffect(() => {
    analyticsService.trackNativePageview()
    const handleRouteChange = () => analyticsService.trackNativePageview()
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  const getLayout = Component.getLayout ?? ((page) => <Layout>{page}</Layout>)

  return (
    <div className={font.className}>
      <Head>
        <title>ALOE</title>

        {process.env.NEXT_PUBLIC_ENV !== 'local' && (
          <script
            dangerouslySetInnerHTML={{
              // Clarity pinned to 0.8.67 to fix the Chrome-for-iOS "Maximum call
              // stack" crash (microsoft/clarity#1103, Sentry ALOESUITE-PROD-BR).
              // The auto-update tag (clarity.ms/tag/s983huzekm) still serves the
              // buggy 0.8.66; this inlines the exact bootstrap the tag returns,
              // with only the SDK version bumped, so consent/masking config is
              // unchanged. TODO(DEV-794): revert to the tag loader once Microsoft
              // promotes 0.8.67+ to the tag.
              //
              // Run-once guard (window.__aloeClarityBooted): Next's pages-router
              // re-executes inline <Head> scripts on every client-side navigation
              // (head-manager re-inserts the <script>). The bootstrap is NOT
              // idempotent: on re-run `window.clarity` is already the real SDK, its
              // `.q` queue was drained, and `a[c].q.unshift(a[c].q.pop())` throws
              // "Cannot read properties of undefined (reading 'unshift')" (Sentry
              // ALOESUITE-PROD-KX/KZ/M0, ~77 users). The bootstrap's own `.v/.t`
              // dedup guard doesn't cover this window, so we gate the whole thing
              // on our own flag and run it exactly once per page load.
              __html: `if(!window.__aloeClarityBooted){window.__aloeClarityBooted=1;
            (function(c,l,a){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)}})(window,document,"clarity");
            !function(c,l,a,r,i,t,y){a[c]("metadata",(function(){a[c]("set","C_IS","0")}),!1,!0);if(a[c].v||a[c].t)return a[c]("event",c,"dup."+i.projectId);a[c].t=!0,(t=l.createElement(r)).async=!0,t.src="https://scripts.clarity.ms/0.8.67/clarity.js",(y=l.getElementsByTagName(r)[0]).parentNode.insertBefore(t,y),a[c]("start",i),a[c].q.unshift(a[c].q.pop()),a[c]("set","C_IS","0")}("clarity",document,window,"script",{"projectId":"s983huzekm","upload":"https://z.clarity.ms/collect","expire":365,"cookies":["_uetmsclkid","_uetvid","_clck"],"track":false,"content":true,"unmask":["body"],"dob":2381});}`,
            }}
          />
        )}
      </Head>

      <AuthContextProvider>
        <NextNProgress color="#004225" height={2} />

        {getLayout(<Component {...pageProps} />)}

        <Toaster />
      </AuthContextProvider>
    </div>
  )
}

export default appWithTranslation(App)
